// cspell:disable
import { datadogLogs } from "@datadog/browser-logs";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoadWithInput } from "@redotech/react-util/load";
import { useStore } from "@redotech/react-util/store";
import { usePendingUpdates } from "@redotech/react-util/use-pending-updates";
import FlipBackwardIcon from "@redotech/redo-icons/flip-backward_filled.svg";
import ExternalLinkIcon from "@redotech/redo-icons/link-external-02.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import { redoCodeFormat } from "@redotech/redo-model/code";
import {
  FulfillmentGroupData,
  IFulfillmentGroupData,
} from "@redotech/redo-model/fulfillments/fulfillment-group";
import {
  FulfillmentGroupStatus,
  HiddenStatuses,
  OpenLikeStatuses,
} from "@redotech/redo-model/fulfillments/fulfillment-group-status";
import {
  FulfillmentOrderLineItem,
  groupFulfillmentOrderLineItems,
  ungroupFulfillmentOrderLineItems,
} from "@redotech/redo-model/fulfillments/fulfillment-order-line-item";
import { AttributesForNewFulfillmentGroups } from "@redotech/redo-model/fulfillments/split-fulfillment-group";
import {
  getToteIdsForFulfillmentGroup,
  PickSessionStatus,
  PickSessionStrategy,
} from "@redotech/redo-model/wms/schema/pick-session";
import {
  alertOnFailure,
  hideNotification,
  showNotification,
  toast,
} from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import { RedoProgressBar } from "@redotech/redo-web/arbiter-components/progress-bar/redo-progress-bar";
import { Flex } from "@redotech/redo-web/flex";
import { NotificationInstance } from "@redotech/redo-web/notification-provider";
import {
  ERROR_SOUND,
  SUCCESS_SOUND,
  SUCCESS_SOUND_2,
} from "@redotech/redo-web/sound/sounds";
import { Text } from "@redotech/redo-web/text";
import { Input } from "@redotech/ui/form";
import { ImmutableCountMap } from "@redotech/util/map";
import { ObjectId } from "bson";
import {
  createContext,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PinVerificationProvider } from "../../common/pin-verification-context";
import { usePackingSlipPredictions } from "../../hooks/use-packing-slip-predictions";
import { useRetryOnResolve } from "../../hooks/use-retry-on-resolve";
import { useWatchStream } from "../../hooks/use-watch-stream";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import { PickSessionOrdersModal } from "../../pick-sessions/pick-session-orders-modal";
import { isItemMatch } from "../../pick-sessions/util";
import { getOptimisticUpdates } from "../../utils/get-optimistic-updates";
import { getUserToOmsUser } from "../../utils/get-team-oms-users";
import { updateFulfillmentGroupsWithAlertFailure } from "../../utils/update-fulfillment-groups-with-retry";
import { ScanMode, scanModeStore } from "../common";
import { ScanningModals } from "../modals/modals";
import { AttributesForSplitShipmentModalData } from "../modals/partially-fulfill-modal";
import { initialScanningState, scanningStateReducer } from "./reducer";
import * as styles from "./styles.module.css";
import {
  Scanned,
  ScanningActionType,
  ScanningContextType,
  ScanningModal,
} from "./types";

enum OrderAction {
  MarkAsProblem = "mark_as_problem",
  ReleaseFromPickSession = "release_from_pick_session",
}

const showScanNotification = (
  notificationInstance: NotificationInstance,
  timeout?: number,
) => {
  hideNotification();
  showNotification(notificationInstance);
  if (timeout) {
    setTimeout(() => {
      hideNotification();
    }, timeout);
  }
};

const getScannedNotificationInstance = (
  item: FulfillmentOrderLineItem,
  remaining: number,
): NotificationInstance => {
  return {
    title: `Scanned: ${item.productTitle}`,
    description: item.variantTitle,
    children: () => (
      <Flex>
        <Text color="secondary" fontSize="sm">
          {`${remaining} remaining`}
        </Text>
      </Flex>
    ),
    fullModalImage: item.imageUrl ?? undefined,
  };
};

const emptyContext: ScanningContextType = {
  handleScan: async () => {},
  handlePartialFulfill: () => {},
  scanned: ImmutableCountMap.empty(),
  scanMode: ScanMode.VERIFY_PRINT,
  pending: false,
  setScanned: () => {},
  setScanMode: () => {},
  setFulfillmentGroupData: () => {},
  reloadFulfillmentGroupData: () => {},
  updateFulfillmentGroups: () => {},
  updatesPending: false,
  fulfillmentGroupDataPending: false,
  handleActionButtonClick: () => {},
  setFulfillmentActionButton: () => {},
};

export const ScanningContext = createContext<ScanningContextType>(emptyContext);

export const ScanningContextProvider = memo(function ScanningContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const params = useParams();

  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const team = useRequiredContext(TeamContext);
  const user = useContext(UserContext);

  const wmsEnabled = team.settings.outboundLabels.wms?.enabled;

  const value: ScanningContextType = { ...emptyContext };

  const { loadPredictions, getGroupsByOrderName, getCachedGroup } =
    usePackingSlipPredictions();

  const [state, dispatch] = useReducer(
    scanningStateReducer,
    initialScanningState,
  );

  const [navigationIntent, setNavigationIntent] = useState<{
    resetScanned?: boolean;
    verifyScanned?: boolean;
    verifyAll?: boolean;
    requestSource?: "scan";
  } | null>(null);

  const [showPickSessionOrdersModal, setShowPickSessionOrdersModal] =
    useState(false);
  const [orderActionsDropdownOpen, setOrderActionsDropdownOpen] =
    useState(false);
  const [orderActionsButtonRef, setOrderActionsButtonRef] =
    useState<HTMLButtonElement | null>(null);

  const lastModalActionRef = useRef<ScanningModal | undefined>();

  const {
    fulfillmentGroups,
    modalState,
    modalData,
    pickSession,
    relevantFulfillmentGroups,
    scanned,
  } = state;
  value.modalState = modalState;
  value.fulfillmentGroupData = state.fulfillmentGroupData;

  const fulfillmentGroupId = params.fulfillmentGroupId;

  /*
   * Scan mode
   *
   * While we handle scanMode in the local storage store, we're providing it
   * here to centralize the scanning state from the context of the consumer
   */
  let [scanMode, setScanMode] = useStore(scanModeStore);
  scanMode ??= ScanMode.VERIFY_PRINT;
  value.scanMode = scanMode;
  value.setScanMode = setScanMode;

  /*
   * Packages
   *
   * We load the packages here as they can be scanned to change
   */
  const packages = useLoad(async () => {
    if (!client) return [];

    const [customParcels, carrierParcels] = await Promise.all([
      client.getParcels(null),
      client.getCarrierParcels(null),
    ]);

    return [
      ...(customParcels?.parcels ?? []),
      ...(carrierParcels?.carrierParcels ?? []),
    ];
  }, [state.fulfillmentGroupData?._id]);

  // Fulfillment group data loading
  const [{ pending: fulfillmentGroupDataPending }, loadFulfillmentGroupData] =
    useTriggerLoadWithInput(
      async (
        {
          fulfillmentGroupId,
          requestSource,
          verifyAll,
          resetScanned,
        }: {
          requestSource?: "scan";
          fulfillmentGroupId?: string;
          verifyAll?: boolean;
          resetScanned?: boolean;
        },
        signal,
      ) => {
        try {
          if (!fulfillmentGroupId) {
            return null;
          }
          const cachedGroup = getCachedGroup(fulfillmentGroupId);

          if (cachedGroup && !HiddenStatuses.includes(cachedGroup.status)) {
            datadogLogs.logger.info("Cache hit - fulfillment group ID", {
              teamId: team?._id,
              userId: user?._id,
              fulfillmentGroupId,
              cachedStatus: cachedGroup.status,
              source: "fulfillment-app",
              cacheType: "preload-groups",
              action: "cache-hit",
            });
          } else {
            datadogLogs.logger.info("Cache miss - fulfillment group ID", {
              teamId: team?._id,
              userId: user?._id,
              fulfillmentGroupId,
              cachedGroupExists: !!cachedGroup,
              cachedGroupStatus: cachedGroup?.status,
              source: "fulfillment-app",
              cacheType: "preload-groups",
              action: "cache-miss",
            });
          }

          const { fulfillmentGroup, originalGroupWasMerged } =
            await client.getLatestOpenFulfillmentGroup(
              { fulfillmentGroupId, requestSource },
              { signal },
            );

          // Store originalGroupWasMerged for PIN verification check
          if (originalGroupWasMerged) {
            dispatch({
              type: ScanningActionType.OPEN_MODAL,
              payload: {
                type: ScanningModal.MERGED_PACKING_SLIP_SCANNED,
                data: fulfillmentGroup,
              },
            });
            return null;
          }

          if (fulfillmentGroup._id !== fulfillmentGroupId) {
            return handleNavigate(fulfillmentGroup._id, {
              resetScanned,
            });
          }

          const { assignedUser } = fulfillmentGroup;

          if (user && (!assignedUser || assignedUser._id === "unassigned")) {
            await updateFulfillmentGroupsWithAlertFailure(
              client,
              [{ fulfillmentGroupId, assignedUser: getUserToOmsUser(user) }],
              { teamId: team?._id },
            );
          }

          if (fulfillmentGroup.status === FulfillmentGroupStatus.Closed) {
            dispatch({
              type: ScanningActionType.OPEN_MODAL,
              payload: { type: ScanningModal.CLOSED_GROUP_WARNING },
            });
          }

          dispatch({
            type: ScanningActionType.SET_FULFILLMENT_GROUP_DATA,
            payload: fulfillmentGroup,
          });

          void loadPredictions(fulfillmentGroup._id);

          if (verifyAll || (resetScanned && fulfillmentGroup.scanVerified)) {
            const lineItems = groupFulfillmentOrderLineItems(
              fulfillmentGroup.items,
            );
            setScanned(
              ImmutableCountMap.fromEntries(
                lineItems.map((item) => [item.externalId, item.quantity]),
              ),
              fulfillmentGroup,
            );
          } else if (resetScanned) {
            setScanned(ImmutableCountMap.empty(), fulfillmentGroup);
          }

          return fulfillmentGroup;
        } catch (error) {
          datadogLogs.logger.error(
            "Error accessing scan page from fulfillment group data load",
            { error },
          );
          if ((error as any).status === 401) {
            toast("Access denied", {
              variant: "error",
              duration: Temporal.Duration.from({ seconds: 5 }),
            });
          } else if ((error as any).message?.toLowerCase().includes("abort")) {
            datadogLogs.logger.error("AbortError", { error });
          } else {
            toast("Redo encountered an error, please try again", {
              variant: "error",
              duration: Temporal.Duration.from({ seconds: 5 }),
            });
            navigate("../scan");
          }

          return null;
        }
      },
    );
  const reloadFulfillmentGroupData = useHandler(() => {
    loadFulfillmentGroupData({ fulfillmentGroupId });
  });
  value.reloadFulfillmentGroupData = reloadFulfillmentGroupData;
  value.fulfillmentGroupDataPending = fulfillmentGroupDataPending;
  const setFulfillmentGroupData = useHandler(
    (data: FulfillmentGroupData | null) => {
      dispatch({
        type: ScanningActionType.SET_FULFILLMENT_GROUP_DATA,
        payload: data,
      });
    },
  );
  value.setFulfillmentGroupData = setFulfillmentGroupData;

  const updateFulfillmentGroupData = useHandler(
    (fulfillmentGroupUpdates: FulfillmentGroupUpdate[]) => {
      if (!state.fulfillmentGroupData) {
        return;
      }
      const { newData, updates } = getOptimisticUpdates(
        [state.fulfillmentGroupData],
        fulfillmentGroupUpdates,
      );
      dispatch({
        type: ScanningActionType.SET_FULFILLMENT_GROUP_DATA,
        payload: newData[0],
      });
      addPendingUpdates(updates);
    },
  );
  value.updateFulfillmentGroups = updateFulfillmentGroupData;

  useEffect(() => {
    if (!fulfillmentGroupId) {
      datadogLogs.logger.info("Clearing fulfillment group data", {
        fulfillmentGroupId,
      });
      dispatch({
        type: ScanningActionType.SET_FULFILLMENT_GROUP_DATA,
        payload: null,
      });
      return;
    }

    // Always set the current fulfillment group when URL changes
    if (state._fulfillmentGroupId !== fulfillmentGroupId) {
      dispatch({
        type: ScanningActionType.SET_FULFILLMENT_GROUP,
        payload: fulfillmentGroupId,
      });
    }

    // Don't reload if we already have the correct group loaded
    if (state.fulfillmentGroupData?._id === fulfillmentGroupId) {
      setNavigationIntent(null);
      return;
    }

    loadFulfillmentGroupData({
      fulfillmentGroupId,
      requestSource: navigationIntent?.requestSource,
      resetScanned: navigationIntent?.resetScanned,
      verifyAll: navigationIntent?.verifyAll,
    });

    setNavigationIntent(null);
    // We don't want to rerun the useEffect when we set the navigation intent
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [fulfillmentGroupId, loadFulfillmentGroupData, state._fulfillmentGroupId]);

  const pendingFulfillmentGroups = useWatchStream(
    client,
    "watchFulfillmentGroups",
    { fulfillmentGroupIds: state.fulfillmentGroupIds },
    ({ fulfillmentGroups }) => {
      dispatch({
        type: ScanningActionType.SET_FULFILLMENT_GROUPS,
        payload: fulfillmentGroups,
      });
    },
  );

  const allFulfillmentGroupsLoaded = state.fulfillmentGroupIds.every(
    (id) => state.fulfillmentGroups[id],
  );

  /*
   * Fulfillment action button
   *
   * The fulfillment action button is the primary action button on the
   * fulfillment group page. We're using it to print labels once we've
   * fulfilled all items when enabled.
   */
  const [fulfillmentActionButton, setFulfillmentActionButton] =
    useState<HTMLButtonElement | null>(null);
  value.setFulfillmentActionButton = setFulfillmentActionButton;

  /*
   * Handle navigation
   *
   * Unifies navigation from within the scan routes, if we're scanning
   * to a new  group, kick off a reload before navigation occurs
   */
  const handleNavigate = useHandler(
    async (
      value: string,
      {
        resetScanned,
        verifyScanned,
        verifyAll,
      }: {
        resetScanned?: boolean;
        verifyScanned?: boolean;
        verifyAll?: boolean;
      } = {
        resetScanned: true,
        verifyScanned: false,
      },
    ) => {
      if (verifyScanned && state.fulfillmentGroupData) {
        /*
         * Handle navigation if verified
         *
         * If we're in verify print mode, and we haven't scanned all items, we verify
         * with the user before navigating
         */
        const allScanned =
          scanned.sum() === state.fulfillmentGroupData.totalItemQuantity;
        const isGroupOpen = OpenLikeStatuses.includes(
          state.fulfillmentGroupData.status,
        );
        if (!allScanned && scanMode === ScanMode.VERIFY_PRINT && isGroupOpen) {
          lastModalActionRef.current = ScanningModal.INCOMPLETE_VERIFICATION;
          dispatch({
            type: ScanningActionType.OPEN_MODAL,
            payload: {
              type: ScanningModal.INCOMPLETE_VERIFICATION,
              data: value,
            },
          });
          return;
        }
      }

      if (ObjectId.isValid(value)) {
        dispatch({
          type: ScanningActionType.SET_FULFILLMENT_GROUP,
          payload: value,
        });
      }

      setNavigationIntent({
        resetScanned,
        verifyScanned,
        verifyAll,
        requestSource: "scan",
      });

      const pathPrefix = fulfillmentGroupId ? "../" : "";
      const path = `${pathPrefix}${value}`;
      navigate(path, { relative: "path" });
    },
  );

  /*
   * Handle fulfillment group selection
   *
   * Unifies fulfillment group selection from within the scan routes
   */
  const handleSelectFulfillmentGroup = useHandler(
    async ({ _id: fulfillmentGroupId, assignedUser }: FulfillmentGroupData) => {
      if (user && (!assignedUser || assignedUser._id === "unassigned")) {
        try {
          await updateFulfillmentGroupsWithAlertFailure(
            client,
            [{ fulfillmentGroupId, assignedUser: getUserToOmsUser(user) }],
            { teamId: team?._id },
          );
        } catch (error) {
          console.error(
            "Error updating fulfillment group from scan start page",
            error,
          );
          throw error;
        }
      }

      await handleNavigate(fulfillmentGroupId, {
        resetScanned: true,
        verifyScanned: !!params.fulfillmentGroupId, // Only verify if already on a group page
      });
    },
  );

  // Update groups with optimistic updates and pending updates tracking
  const updateGroups = useHandler(async (updates: FulfillmentGroupUpdate[]) => {
    try {
      const updatedGroups = await updateFulfillmentGroupsWithAlertFailure(
        client,
        updates,
        { teamId: team?._id },
      );
      const updatedGroupsArray = Object.values(updatedGroups);
      if (updatedGroupsArray.length > 0) {
        dispatch({
          type: ScanningActionType.SET_FULFILLMENT_GROUP_DATA,
          payload: updatedGroupsArray[0],
        });
      }
    } catch (error) {
      console.error("Error updating fulfillment groups from scan page", error);
      throw error;
    }
  });

  const { updatesPending, addPendingUpdates } = usePendingUpdates({
    updateGroups,
    identifier: (update) => update.fulfillmentGroupId,
  });
  value.updatesPending = updatesPending;

  // fulfillmentActionButton may be disabled when we first load the page,
  // wait for it to be enabled before we try to click it
  const [, handleActionButtonClick] = useRetryOnResolve(
    fulfillmentGroupDataPending,
    async () => {
      // We use fulfillmentActionButton.click() to get the validation and action
      // selection logic provided by the button itself
      fulfillmentActionButton?.click();
    },
  );
  value.handleActionButtonClick = handleActionButtonClick;

  const handleSelectPickSessionItem = useHandler((value: string): boolean => {
    if (!pickSession) {
      return false;
    }

    const item = pickSession.items.find((item) => {
      // If we have a specific fulfillment group selected, only match items from that group
      if (state.fulfillmentGroupData?._id) {
        if (item.fulfillmentGroupId !== state.fulfillmentGroupData._id) {
          datadogLogs.logger.info("Not the same fulfillment group", {
            itemFgId: item.fulfillmentGroupId,
            fulfillmentGroupId: state.fulfillmentGroupData._id,
          });
          return false;
        }
        if (!OpenLikeStatuses.includes(state.fulfillmentGroupData.status)) {
          datadogLogs.logger.info("Not in OpenLike status", {
            itemFgId: item.fulfillmentGroupId,
            fulfillmentGroupId: state.fulfillmentGroupData._id,
          });
          return false;
        }
      }

      // Check if the item's fulfillment group is loaded and in an open status
      const itemFulfillmentGroup =
        state.fulfillmentGroups[item.fulfillmentGroupId];
      if (
        !itemFulfillmentGroup ||
        !OpenLikeStatuses.includes(itemFulfillmentGroup.status)
      ) {
        return false;
      }

      return isItemMatch(item, value, team);
    });

    datadogLogs.logger.info("handleSelectPickSessionItem", { item });

    if (!item || !item.externalId) {
      const newItem = pickSession?.items.find(
        (item) =>
          isItemMatch(item, value, team) &&
          OpenLikeStatuses.includes(
            state.fulfillmentGroups[item.fulfillmentGroupId].status,
          ),
      );
      if (newItem) {
        void handleNavigate(newItem.fulfillmentGroupId, {
          verifyScanned: true,
          verifyAll: true,
        });

        return true;
      }

      return false;
    } else if (scanned.get(item.externalId) >= item.totalQuantity) {
      datadogLogs.logger.info("Item already scanned", {
        itemExternalId: item.externalId,
        itemTotalQuantity: item.totalQuantity,
        scannedQuantity: scanned.get(item.externalId),
      });
      return false;
    }

    if (item.fulfillmentGroupId === fulfillmentGroupId) {
      setScanned(scanned.increment(item.externalId, 1));
      return true;
    }

    const newScanned = ImmutableCountMap.empty<string, number>();
    setScanned(
      newScanned.increment(item.externalId, 1),
      state.fulfillmentGroups[item.fulfillmentGroupId],
    );
    void handleNavigate(item.fulfillmentGroupId, {
      verifyScanned: !!fulfillmentGroupId,
    });
    return true;
  });

  /*
   * =======================================================================
   * SCANNING LOGIC
   * =======================================================================
   *
   * The following handlers are responsible for handling the actual scanning
   * logic for the different workflows below the scan route
   *
   * - Tote item selection modal
   * - Start page
   * - Fulfillment group page
   */

  /*
   * Handle tote order selection
   *
   * After a tote is scanned, we need to handle an item scan specifically pulling
   * the item from the active pick session
   */
  const handleToteOrderSelectionScan = useHandler(async (value?: string) => {
    if (!pickSession || !value) {
      // If we don't have a pick session, we can't select an item
      return;
    }

    // If we have a pick session, we need to find the item
    try {
      const item = handleSelectPickSessionItem(value);

      if (item) {
        // Don't close INCOMPLETE_VERIFICATION modal
        if (
          lastModalActionRef.current !==
            ScanningModal.INCOMPLETE_VERIFICATION &&
          state.modalState === ScanningModal.TOTE_ORDER_SELECTION
        ) {
          dispatch({ type: ScanningActionType.CLOSE_MODAL });
        }
      } else {
        // Item not found in pick session - clear the pick session and try normal scanning
        dispatch({ type: ScanningActionType.CLEAR_PICK_SESSION });
        await handleToteOrFulfillmentGroupScan(value);
      }
    } catch (error) {
      // If we throw, we don't have an item to select
      return;
    }
  });
  /*
   * if we scan an item too quickly after scanning a tote,
   * we may try to submit before we've loaded the relevant
   * fulfillment groups, set let's try once we have them
   */
  const [{ pending: toteOrderSelectionPending }, handleToteOrderSelection] =
    useRetryOnResolve(
      pendingFulfillmentGroups || !allFulfillmentGroupsLoaded,
      handleToteOrderSelectionScan,
    );

  /*
   * Handle tote or fulfillment group scanning
   *
   * Any time we are scanning in a new order, we could also be scanning in a tote.
   */
  const handleToteOrFulfillmentGroupScan = useHandler(async (value: string) => {
    try {
      /*
       * Handle tote scanning
       *
       * If a tote is scanned, we pull the fulfillment group id from the tote
       */
      let fulfillmentGroupId = value;

      if (wmsEnabled) {
        try {
          const { pickSession, tote } =
            await client.getPickSessionByToteBarcode({ barcode: value });

          /*
           * If we didn't throw, but we didn't find a pick session,
           * the tote isn't in an active pick session
           */
          if (!tote || !pickSession) {
            toast("Tote not in an active pick session", { variant: "error" });
            return;
          }

          /**
           * If the pick session is active, check if all of the items have been assigned to totes.
           * If so, mark the pick session as picked (the picker likely scanned everything but
           * never marked the pick session as picked)
           */
          if (pickSession.status === PickSessionStatus.ACTIVE) {
            const allItemsAssignedToTotes = pickSession.items.every((item) => {
              const itemQuantityNeedingAssignment =
                item.totalQuantity - item.quantityOutOfStock;
              const itemQuantityAssigned = Object.values(
                item.quantityByTote ?? {},
              ).reduce((sum, { quantity }) => sum + quantity, 0);
              return itemQuantityNeedingAssignment === itemQuantityAssigned;
            });
            if (allItemsAssignedToTotes) {
              try {
                await client.markPickSessionAsPicked({
                  pickSessionId: pickSession._id,
                });
              } catch (error) {
                console.error(
                  "Error automatically marking pick session as picked",
                  error,
                );
                toast(
                  "This tote's pick session is still active. Please mark the pick session as picked manually.",
                  { variant: "error" },
                );
                return;
              }
            } else {
              toast(
                "Not all items in this tote's pick session have been picked and assigned to totes. Complete the pick session before scanning this tote.",
                { variant: "error" },
              );
              return;
            }
          }

          /*
           * If the pick session is not in a picked or printed state, we can't verify
           * the tote orders
           */
          if (
            pickSession.status !== PickSessionStatus.PICKED &&
            pickSession.status !== PickSessionStatus.PRINTED &&
            pickSession.status !== PickSessionStatus.PROBLEM &&
            pickSession.status !== PickSessionStatus.ACTIVE // handled above
          ) {
            toast(
              "Tote's pick session is not in a picked, printed, or problem state",
              {
                variant: "error",
              },
            );
            return;
          }

          /*
           * If the pick session is a single item pick session, we navigate
           * now work within the context of the pick session, each item scan should load
           * a fulfillment group that contains the item, as well as set it as scanned
           */
          if (pickSession.strategy === PickSessionStrategy.SINGLE_ITEM) {
            dispatch({
              type: ScanningActionType.SET_PICK_SESSION,
              payload: pickSession,
            });

            return;
          }

          /*
           * If the pick session is a multi-item pick session, we need to find the item's
           * fulfillment group. we currently assume that the tote will contain a single
           * fulfillment group, and that a fulfillment group won't span multiple totes,
           * so we handle as though we were scanning a fulfillment group id
           */
          const toteId = tote._id;
          const toteItem = pickSession.items.find(
            (item) =>
              item.quantityByTote &&
              Object.keys(item.quantityByTote).includes(toteId),
          );

          if (!toteItem) {
            // If we get here, we have an issue, the pick session we pull from the tote
            // SHOULD be associated with at least one item in the tote
            toast("No items found in valid tote", { variant: "error" });
            return;
          }

          ({ fulfillmentGroupId } = toteItem);

          // Store the pick session for multi-item strategy so we can show order actions
          // Use UPDATE_PICK_SESSION instead of SET_PICK_SESSION to avoid opening the tote order selection modal
          dispatch({
            type: ScanningActionType.UPDATE_PICK_SESSION,
            payload: pickSession,
          });

          // Check if this fulfillment group spans multiple totes
          const allToteIds = [
            ...new Set(
              getToteIdsForFulfillmentGroup(pickSession, fulfillmentGroupId),
            ),
          ];

          if (allToteIds.length > 1) {
            // Fetch all totes to get their names
            const { totes: allTotesForThisGroup } = await client.getTotesByIds({
              toteIds: allToteIds,
            });

            // Build tote names map from fetched totes
            const toteNames: Record<string, string> = {};
            for (const t of allTotesForThisGroup) {
              toteNames[t._id] = t.name;
            }

            dispatch({
              type: ScanningActionType.OPEN_MODAL,
              payload: {
                type: ScanningModal.MULTI_TOTE_VERIFICATION,
                data: {
                  toteIds: allToteIds,
                  toteNames,
                  scannedToteId: toteId,
                  fulfillmentGroupId,
                },
              },
            });
            return;
          }
        } catch (error) {
          // If we throw here, we're not scanning a tote
        }
      }

      /*
       * Handle fulfillment group navigation
       *
       * whether we scanned a tote or a fulfillment group id, we now need to load the
       * fulfillment group
       */

      const { fulfillmentGroups } =
        await client.getRelevantFulfillmentGroupData({
          identifier: fulfillmentGroupId,
        });

      /*
       * If we don't find a valid scan from the fulfillment page, we throw a toast or open
       * a modal, AND play a sound.
       */
      if (fulfillmentGroups.length === 0) {
        console.error("No match found for scanned barcode", {
          scannedValue: value,
          groupId: state.fulfillmentGroupData?._id,
          groupLineItems: state.fulfillmentGroupData?.items.map((li) => ({
            barcode: li.barcode,
            sku: li.sku,
            productTitle: li.productTitle,
          })),
        });

        if (params.fulfillmentGroupId) {
          dispatch({
            type: ScanningActionType.OPEN_MODAL,
            payload: { type: ScanningModal.WRONG_BARCODE, data: value },
          });
        } else {
          toast("No shipments found", { variant: "error" });
        }

        ERROR_SOUND.currentTime = 0;
        void ERROR_SOUND.play();
        return;
      }

      /*
       * If we have multiple fulfillment groups, we need to select one
       */
      if (fulfillmentGroups.length > 1) {
        dispatch({
          type: ScanningActionType.SELECT_FULFILLMENT_GROUP,
          payload: fulfillmentGroups,
        });
      } else {
        await handleSelectFulfillmentGroup(fulfillmentGroups[0]);
      }
    } catch (err) {
      if (err instanceof Error && err.message === "Pick session found") {
        return;
      }
      console.error(
        "Error accessing scan page from tote or fulfillment group scan",
        err,
      );
      if ((err as any).statusCode === 401) {
        toast("Access denied", { variant: "error" });
        return;
      }
    }
  });

  /*
   * Handle fulfillment page scanning
   *
   * From the fulfillment page we allow the scanning of:
   *  - Items to verify items
   *  - Items on other fulfillment groups in a pick session to navigate to them
   *  - Package/parcel hashed names to update selected parcels
   *  - Order numbers
   */
  const [{ pending: fulfillmentPageScanPending }, handleFulfillmentPageScan] =
    useTriggerLoadWithInput(async (value: string) => {
      if (!state.fulfillmentGroupData) {
        return;
      }

      /*
       * Handle package/parcel hashed names
       */
      if (
        packages.value?.some(
          (parcel) => parcel.hashedName?.toLowerCase() === value.toLowerCase(),
        ) &&
        state.fulfillmentGroupData.status !== FulfillmentGroupStatus.Closed
      ) {
        const scannedParcel = packages.value?.find(
          (parcel) => parcel.hashedName?.toLowerCase() === value.toLowerCase(),
        );
        if (scannedParcel) {
          datadogLogs.logger.info("Package shortcut scanned", {
            teamId: team?._id,
            userId: user?._id,
            fulfillmentGroupId: state.fulfillmentGroupData._id,
            orderId: state.fulfillmentGroupData._id,
            packageName: scannedParcel.name,
            packageHashedName: scannedParcel.hashedName,
            scanValue: value,
            source: "fulfillment-app",
            shortcutType: "scan",
            action: "Select package/parcel",
          });

          await updateFulfillmentGroupsWithAlertFailure(
            client,
            [
              {
                fulfillmentGroupId: state.fulfillmentGroupData._id,
                selectedParcels: [scannedParcel],
              },
            ],
            { teamId: team?._id },
          );

          // Check if we should auto-print when scanning to select a package
          if (
            team?.settings.outboundLabels.printOnPackageScan &&
            scanned.sum() >= state.fulfillmentGroupData.totalItemQuantity
          ) {
            handleActionButtonClick();
          }
        } else {
          toast("Parcel not found", { variant: "error" });
        }

        return;
      }

      /*
       * Handle navigating to other fulfillment groups in a pick session
       *
       * We're running this before checking for items in the fulfilment group
       * to avoid over incrementing quantities for another fulfillment group
       * that contains the same item
       *
       * should only actually run when we have a pick session
       */
      if (wmsEnabled && state.pickSession) {
        try {
          const found = handleSelectPickSessionItem(value);
          if (found) {
            return;
          }
        } catch (error) {
          // If we throw, we don't have an item to select
          return;
        }
      }

      /*
       * Handle verifying items
       */
      const matchedItem: FulfillmentOrderLineItem | undefined =
        groupFulfillmentOrderLineItems(state.fulfillmentGroupData.items).find(
          (item) => isItemMatch(item, value, team),
        );
      if (matchedItem) {
        // Check if item is already fully scanned before allowing more scans
        const currentScannedCount = scanned.get(matchedItem.externalId);

        if (currentScannedCount >= matchedItem.quantity) {
          dispatch({
            type: ScanningActionType.OPEN_MODAL,
            payload: { type: ScanningModal.DUPLICATE_BARCODE, data: value },
          });
          return;
        }

        const newScanned = scanned.increment(matchedItem.externalId, 1);
        const currentScannedValue = newScanned.get(matchedItem.externalId);
        const currentScannedSum = newScanned.sum();
        setScanned(newScanned);

        // Log item scan
        datadogLogs.logger.info("Item scanned for verification", {
          teamId: team?._id,
          userId: user?._id,
          fulfillmentGroupId: state.fulfillmentGroupData._id,
          orderId: state.fulfillmentGroupData._id,
          itemExternalId: matchedItem.externalId,
          itemName: matchedItem.productTitle,
          itemSku: matchedItem.sku,
          scannedCount: currentScannedValue,
          totalRequired: matchedItem.quantity,
          allItemsScanned:
            currentScannedSum === state.fulfillmentGroupData.totalItemQuantity,
          scanValue: value,
          source: "fulfillment-app",
        });

        const showNotifications =
          team?.settings.outboundLabels.showScanNotifications ?? true;
        if (showNotifications) {
          showScanNotification(
            getScannedNotificationInstance(
              matchedItem,
              matchedItem.quantity - currentScannedValue,
            ),
            5000,
          );
        }

        if (
          currentScannedSum === state.fulfillmentGroupData.totalItemQuantity
        ) {
          SUCCESS_SOUND_2.currentTime = 0;
          void SUCCESS_SOUND_2.play();

          if (team?.settings.outboundLabels.printLabelOnAllScanVerified) {
            handleActionButtonClick();
          }
        } else {
          SUCCESS_SOUND.currentTime = 0;
          void SUCCESS_SOUND.play();
        }

        return;
      }

      /*
       * Handle fulfillment group navigation
       *
       * We assume that if the scanned value is a valid object id, it's a
       * fulfillment group id. We also check for cached order names.
       */
      if (ObjectId.isValid(value)) {
        await handleNavigate(value, {
          verifyScanned: true,
          resetScanned: true,
        });
        return;
      }

      // Check if we have cached groups for this order name
      const cachedGroups = getGroupsByOrderName(value);
      if (cachedGroups.length > 0) {
        datadogLogs.logger.info("Cache hit - order name", {
          teamId: team?._id,
          userId: user?._id,
          orderName: value,
          cachedGroupCount: cachedGroups.length,
          cachedGroupIds: cachedGroups.map((g) => g._id),
          source: "fulfillment-app",
          cacheType: "preload-groups",
          action: "cache-hit",
        });
      } else {
        datadogLogs.logger.info("Cache miss - order name", {
          teamId: team?._id,
          userId: user?._id,
          orderName: value,
          source: "fulfillment-app",
          cacheType: "preload-groups",
          action: "cache-miss",
        });
      }

      try {
        const code = redoCodeFormat.read(value.trim());
        if (code.type === "FF") {
          value = code.id.toString();
        }
      } catch {
        //
      }

      await handleToteOrFulfillmentGroupScan(value);
    });

  /*
   * Handle start page scanning
   *
   * From the start page we allow the scanning of:
   *  - Fulfillment group ids
   *  - Tote ids
   */
  const [{ pending: startPageScanPending }, handleStartPageScan] =
    useTriggerLoadWithInput(async (value: string) => {
      /*
       * Handle fulfillment group navigation
       *
       * We assume that if the scanned value is a valid object id, it's a
       * fulfillment group id.
       */
      if (ObjectId.isValid(value)) {
        datadogLogs.logger.info("Fulfillment group navigation scanned", {
          teamId: team?._id,
          userId: user?._id,
          targetFulfillmentGroupId: value,
          scanValue: value,
          source: "fulfillment-app",
          action: "Navigate to fulfillment group",
        });
        await handleNavigate(value);
        return;
      }

      try {
        const code = redoCodeFormat.read(value.trim());
        if (code.type === "FF") {
          value = code.id.toString();
        }
      } catch {
        //
      }

      await handleToteOrFulfillmentGroupScan(value);
    });

  /*
   * Handle partial fulfill
   *
   * If the user requests to partially fulfill, we verify that request in a modal,
   * As we don't have an asynchronous confirmation modal flow, we handle the actually
   * partial fulfillment request in the modal
   */
  const handlePartialFulfill = useHandler(() => {
    dispatch({
      type: ScanningActionType.OPEN_MODAL,
      payload: { type: ScanningModal.PARTIAL_FULFILL },
    });
  });
  value.handlePartialFulfill = handlePartialFulfill;

  /*
   * Handle order actions for multi-item pick session
   *
   * These actions allow the user to mark the order as not picked or release it
   * from the pick session when viewing a fulfillment group from a multi-item tote scan
   */
  const [{ pending: orderActionPending }, triggerOrderAction] =
    useTriggerLoadWithInput(
      async ({
        fulfillmentGroupId,
        action,
      }: {
        fulfillmentGroupId: string;
        action: OrderAction;
      }) => {
        try {
          if (action === OrderAction.MarkAsProblem) {
            const result = await client.markFulfillmentGroupAsProblem({
              pickSessionId: pickSession!._id.toString(),
              fulfillmentGroupId,
            });
            if (result.success) {
              toast("Order marked not picked", { variant: "success" });
              // Clear pick session and navigate back to scan start
              dispatch({ type: ScanningActionType.CLEAR_PICK_SESSION });
              navigate("../scan");
            } else {
              toast(result.message || "Failed to mark order as problem", {
                variant: "error",
              });
            }
          } else if (action === OrderAction.ReleaseFromPickSession) {
            const result = await client.cancelPicking({
              fulfillmentGroupId,
            });
            if (result.success) {
              toast("Order released from pick session", { variant: "success" });
              // Clear pick session and navigate back to scan start
              dispatch({ type: ScanningActionType.CLEAR_PICK_SESSION });
              navigate("../scan");
            } else {
              toast(result.message || "Failed to release order", {
                variant: "error",
              });
            }
          }
        } catch (error) {
          toast(`Failed to perform action: ${(error as Error).message}`, {
            variant: "error",
          });
        }
      },
    );

  /*
   * Do partial fulfillment
   *
   * This function is called from the verification modal
   */
  const [{ pending: partialFulfillPending }, doPartialFulfill] =
    useTriggerLoadWithInput(
      (partiallyFulfillModalData: AttributesForSplitShipmentModalData) =>
        alertOnFailure("Failed to partially fulfill order")(async () => {
          if (!fulfillmentGroupId) {
            return;
          }
          dispatch({ type: ScanningActionType.CLOSE_MODAL });
          const originalLineItems = state.fulfillmentGroupData!.items;
          const groupedLineItems = groupFulfillmentOrderLineItems(
            originalLineItems,
            { usePrice: true },
          );

          const groupedItemsToSplit = groupedLineItems.map((item) => {
            const count = scanned.get(item.externalId);
            return { ...item, quantity: item.quantity - count };
          });

          const ungrouped = ungroupFulfillmentOrderLineItems(
            originalLineItems,
            groupedItemsToSplit,
          );

          const itemsToSplit = ungrouped.map((item) => ({
            fulfillmentOrderLineItemId: item.externalId,
            quantity: item.quantity,
          }));

          const attributesForNewFulfillmentGroups: AttributesForNewFulfillmentGroups =
            {
              internalNote: partiallyFulfillModalData.internalNote,
              status: partiallyFulfillModalData.newStatus,
              holdDetails:
                partiallyFulfillModalData.newStatus ===
                FulfillmentGroupStatus.OnHold
                  ? {
                      onHoldUntil: partiallyFulfillModalData.onHoldUntil,
                      reason: partiallyFulfillModalData.onHoldReason,
                    }
                  : undefined,
            };

          const { success, warningMessage, newGroupIds } =
            await client.splitFulfillmentGroup({
              originalGroupId: fulfillmentGroupId,
              itemsToSplit,
              attributesForNewFulfillmentGroups,
            });

          if (!success && warningMessage) {
            toast(warningMessage, { variant: "warning" });
            return;
          }

          // Show toast with link to new shipments created from split
          if (newGroupIds) {
            let newGroupIndex = 0;
            for (const groupId of newGroupIds) {
              newGroupIndex++;
              toast(
                <Flex align="center" justify="space-between" w="full">
                  <Text>
                    New shipment {newGroupIndex} created from partial fulfill.
                  </Text>
                  <RedoButton
                    hierarchy="primary"
                    IconTrailing={ExternalLinkIcon}
                    onClick={() => {
                      window.open(
                        `/stores/${team._id}/order-management/outbound-labels/scan/${groupId}`,
                        "_blank",
                      );
                    }}
                    size="xs"
                    text="Go to new shipment"
                  />
                </Flex>,
                { variant: "success", persist: true },
              );
            }
          }

          loadFulfillmentGroupData({ fulfillmentGroupId, verifyAll: true });
        }),
    );
  value.partialFulfillPending = partialFulfillPending;

  /*
   * Pick session progress bar
   *
   * If we are working within the context of a pick session, we want to display a progress bar
   * that shows the user how far along they are in the pick session.
   */
  if (
    pickSession &&
    pickSession.strategy === PickSessionStrategy.SINGLE_ITEM &&
    Object.keys(fulfillmentGroups).length > 0
  ) {
    // Get unique fulfillment group IDs from pick session items (source of truth)
    const pickSessionFulfillmentGroupIds = Array.from(
      new Set(pickSession.items.map((item) => item.fulfillmentGroupId)),
    );
    const fulfilling = pickSessionFulfillmentGroupIds.length;
    const fulfilled = pickSessionFulfillmentGroupIds.reduce((acc, groupId) => {
      const group = fulfillmentGroups[groupId];
      if (group && !OpenLikeStatuses.includes(group.status)) {
        return acc + 1;
      }
      return acc;
    }, 0);

    const percentage = Math.min(
      100,
      Math.max(0, (fulfilled / fulfilling) * 100),
    );

    value.progress = (
      <Flex align="center" dir="row" gap="md">
        <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
          {fulfilled} of {fulfilling} orders fulfilled:
        </Text>
        <Flex className={styles.progressBar} dir="column" justify="center">
          <RedoProgressBar
            fillColor="var(--redo-colors-foreground-fg-success-primary)"
            percentage={Number(percentage.toFixed(2))}
            style="thin"
          />
        </Flex>
        <RedoButton
          hierarchy="link_underline"
          onClick={() => setShowPickSessionOrdersModal(true)}
          size="xs"
          text="View orders"
        />
      </Flex>
    );
  }

  /*
   * Order actions for multi-item pick session
   *
   * When a multi-item tote is scanned, we show order actions in place of the progress bar
   */
  if (
    pickSession &&
    pickSession.strategy === PickSessionStrategy.MULTI_ITEM &&
    fulfillmentGroupId
  ) {
    value.progress = (
      <Flex align="center" dir="row" gap="md">
        <RedoButton
          disabled={orderActionPending}
          hierarchy="link_underline"
          onClick={() => setOrderActionsDropdownOpen(!orderActionsDropdownOpen)}
          ref={setOrderActionsButtonRef}
          size="xs"
          text="Pick session options"
        />
        <RedoListDropdown<OrderAction>
          dropdownAnchor={orderActionsButtonRef}
          dropdownOpen={orderActionsDropdownOpen}
          fitToAnchor={false}
          items={[
            {
              id: OrderAction.MarkAsProblem,
              value: OrderAction.MarkAsProblem,
              type: "text",
              text: "Item not picked",
            },
            {
              id: OrderAction.ReleaseFromPickSession,
              value: OrderAction.ReleaseFromPickSession,
              type: "custom",
              useWrapper: true,
              render: (
                <>
                  <RedoIcon color="red" Icon={FlipBackwardIcon} size={16} />
                  <Text color="red" fontSize="xs" fontWeight="medium">
                    Send order to be repicked
                  </Text>
                </>
              ),
            },
          ]}
          itemSelected={({ item }) => {
            triggerOrderAction({
              fulfillmentGroupId,
              action: item.value,
            });
            setOrderActionsDropdownOpen(false);
          }}
          selectedItems={undefined}
          setDropdownOpen={setOrderActionsDropdownOpen}
        />
      </Flex>
    );
  }

  /*
   * Scanned items
   *
   * We keep track of the scanned items in a central place and handle the
   * verification once all items are scanned
   */
  const setScanned = useHandler(
    (newScanned: Scanned, scanningFulfillmentGroup?: IFulfillmentGroupData) => {
      dispatch({ type: ScanningActionType.SET_SCANNED, payload: newScanned });

      const fulfillmentGroupData =
        scanningFulfillmentGroup ?? state.fulfillmentGroupData;

      const totalItems = fulfillmentGroupData?.totalItemQuantity;
      const currentlyVerified = !!fulfillmentGroupData?.scanVerified;

      if (totalItems !== undefined && user && fulfillmentGroupData?._id) {
        const allItemsScanned = newScanned.sum() === totalItems;
        if (allItemsScanned !== currentlyVerified) {
          updateFulfillmentGroupData([
            {
              fulfillmentGroupId: fulfillmentGroupData._id,
              scanVerified: allItemsScanned
                ? {
                    verifiedBy: {
                      _id: user._id,
                      email: user.email,
                      createdAt: new Date(user.createdAt),
                      firstName: user.firstName,
                      lastName: user.lastName,
                      name: user.name,
                      updatedAt: new Date(user.updatedAt),
                      team: user.team,
                    },
                    verifiedAt: new Date(),
                  }
                : null,
            },
          ]);
        }
      }
    },
  );
  value.scanned = scanned;
  value.setScanned = setScanned;

  /*
   * Herein lies the crux of the state structure.
   *
   * We need to auto scan on load of an order by item scan when within
   * the context of a pick session, so we don't want to clear or even
   * necessarily set the scanned items on load if we are coming from a scan
   * in, however, if we refresh, we want to set the scanned items to match
   * the fulfillment group data.
   *
   * We're deriving reload as entering this page with an empty scanned state,
   * if we navigate through scan, we should set the scanned state to match
   * the fulfillment group data before navigation. If the scanned state SHOULD
   * be empty, and this isn't a reload, we don't need to do anything. This
   * will duplicate some work, but it's not a big deal.
   *
   * When we are resetting the scanned state, we DON'T want to update any
   * verification data, so we pass in an empty object as the fulfillment group
   * data.
   */
  useEffect(() => {
    if (!fulfillmentGroupId) {
      setScanned(ImmutableCountMap.empty(), {} as IFulfillmentGroupData);
    }
  }, [fulfillmentGroupId, setScanned]);
  useEffect(() => {
    const handlePopState = () => {
      // Handling dispatch directly here to keep unsetting verified
      dispatch({
        type: ScanningActionType.SET_SCANNED,
        payload: ImmutableCountMap.empty(),
      });
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    if (
      state.fulfillmentGroupData?._id &&
      scanned.sum() === 0 &&
      state.fulfillmentGroupData?.scanVerified
    ) {
      const lineItems = groupFulfillmentOrderLineItems(
        state.fulfillmentGroupData.items,
      );
      setScanned(
        ImmutableCountMap.fromEntries(
          lineItems.map((item) => [item.externalId, item.quantity]),
        ),
      );
    }
    /*
     * `setScanned` should be consistent across renders, and we only care
     * about the state of `scanned` on change of `state.fulfillmentGroupData._id`
     * so we shouldn't include `setScanned` or `scanned` in the deps array.
     */
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [state.fulfillmentGroupData?._id]);

  /*
   * Scan
   *
   * This is the main scan function. It handles the different states of the app and calls the
   * appropriate scan function.
   */
  const handleScan = useHandler((barcode: Input<string>) => {
    hideNotification();

    const value = barcode.value.trim();
    barcode.setValue("");

    if (modalState === ScanningModal.TOTE_ORDER_SELECTION) {
      handleToteOrderSelection(value);
    } else if (fulfillmentGroupId) {
      handleFulfillmentPageScan(value);
    } else {
      handleStartPageScan(value);
    }
  });
  value.handleScan = handleScan;

  value.pending =
    startPageScanPending ||
    fulfillmentPageScanPending ||
    toteOrderSelectionPending;

  return (
    <PinVerificationProvider>
      <ScanningContext.Provider value={value as ScanningContextType}>
        {children}
        <ScanningModals
          doPartialFulfill={doPartialFulfill}
          handleNavigate={handleNavigate}
          handleSelectFulfillmentGroup={handleSelectFulfillmentGroup}
          onClose={() => dispatch({ type: ScanningActionType.CLOSE_MODAL })}
          openModal={modalState}
          openModalData={modalData}
          relevantFulfillmentGroups={relevantFulfillmentGroups}
        />
        {pickSession && (
          <PickSessionOrdersModal
            fulfillmentGroupIdInView={fulfillmentGroupId}
            handleNavigate={handleNavigate}
            isOpen={showPickSessionOrdersModal}
            onClose={() => setShowPickSessionOrdersModal(false)}
            onPickSessionUpdate={(updatedPickSession) => {
              dispatch({
                type: ScanningActionType.UPDATE_PICK_SESSION,
                payload: updatedPickSession,
              });
            }}
            pickSession={pickSession}
          />
        )}
      </ScanningContext.Provider>
    </PinVerificationProvider>
  );
});
